import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosSecureInstance from "commons/axios/axiosSecureInstance";
import { RootState } from "commons/store/store";
import { downloadBlobFile } from "utils/files/downloadBlobFile";
import {exportFile} from "../../comments/redux/commentsSlice";

interface EcrfDataManagement {
  isLoading: boolean;
  error: any;
}

const initialState: EcrfDataManagement = {
  isLoading: false,
  error: null
};

export const downloadDataExportFile = createAsyncThunk(
  "ecrfDataManagement/downloadDataExportFile",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/data_export/${id}/download`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `edm_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1]);
        }
      }

      downloadBlobFile(new Blob([response.data]), fileName);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

export const exportEcrfData = createAsyncThunk(
  "ecrfDataManagement/exportEcrfData",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/ecrves/${id}/export.xlsx`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `ecrf_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match.length > 1) {
          fileName = match[1];
        }
      }

      downloadBlobFile(new Blob([response.data]), fileName);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

export const exportDrugAllocation = createAsyncThunk(
  "ecrfDataManagement/exportDrugAllocation",
  async (type: 'd' | 'dm', { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/ecrves/export.xlsx/drug-allocation?type=${type}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `ecrf_drug_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match.length > 1) {
          fileName = match[1];
        }
      }

      downloadBlobFile(new Blob([response.data]), fileName);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

type GenerateDataExport = {
  name?: string | undefined;
  ecrfIds?: Array<any>;
  sectionIds?: Array<any>;
  elementIds?: Array<any>;
};

export const generateDataExport = createAsyncThunk(
  'ecrfDataManagement/generateDataExport',
  async (params: GenerateDataExport, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.post(`/api/data_export/generate`, params);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const ecrfDataManagementSlice = createSlice({
  name: 'ecrfDataManagement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(downloadDataExportFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(downloadDataExportFile.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(downloadDataExportFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    builder
      .addCase(exportEcrfData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(exportEcrfData.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(exportEcrfData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    builder
      .addCase(exportDrugAllocation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(exportDrugAllocation.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(exportDrugAllocation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const selectEcrfResourceLoading = (state: RootState) => state.ecrf.ecrfDataManagement.isLoading;

export const {} = ecrfDataManagementSlice.actions;

export default ecrfDataManagementSlice.reducer;
